import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type {
  ActionStatus,
  ActivityActionTranslationKeys,
  ActivityCategory,
} from 'lumen/utils/constants/activity-action-constants';

export interface ActivityAction {
  key: ActivityActionTranslationKeys;
  values: Record<string, string>;
}

export const ACTOR_TYPES = ['system', 'lighthouse_staff'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

export interface UserActor {
  email: string;
  name: string;
  user_id: number;
}

export interface NonUserActor {
  actor_type: ActorType;
  name: string;
}

export type ActivityActor = NonUserActor | UserActor;

export function isNonUserActor(actor: ActivityActor): actor is NonUserActor {
  return 'actor_type' in actor;
}

// Entries in `changes.mapping_changes[]`. `old`/`new` vary by `field`: null,
// a boolean, an `{id, name}` reference, or a plain string.
// `breakdown`/`value_type`/`entry_value` only appear for mapping-entry
// changes (central_mapping_relation_changed_*), not category-structure
// changes (central_mapping_category_changed_*).
export interface MappingChangeDetail {
  breakdown?: string;
  entry_value?: string;
  field: string;
  new: unknown;
  old: unknown;
  value_type?: string;
}

export function isMappingChangeDetailArray(
  value: unknown,
): value is MappingChangeDetail[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        'field' in item &&
        'old' in item &&
        'new' in item,
    )
  );
}

export default class ActivityLog extends Model {
  [Type] = 'activity-log' as const;

  @attr declare action: ActivityAction;
  @attr('string') declare actionStatus: ActionStatus;
  @attr declare actor: ActivityActor;
  @attr('string') declare category: ActivityCategory;
  // Only some categories send `[oldValue, newValue]` tuples per field; others
  // (e.g. central_mapping's batched `mapping_changes`) send an array of
  // change-detail objects instead. Narrow with `isChangeTuple` before use.
  @attr declare changes: Record<string, unknown> | null;
  @attr('string') declare ipAddress: string;
  @attr('string') declare location: string;
  @attr('dayjs') declare timestamp: DayjsTransformReturn;
}
